import React, { useState, useMemo, useEffect } from "react";
import { Plus, Search, Grid, List, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import TableList from "./TableList";
import TableForm from "./TableForm";
import SectionManager from "./SectionManager";
import { ConfirmDialog } from "./UIComponents";
import tableService from "../../../services/tableService";
import sectionService from "../../../services/sectionService";
import { useAuth } from '@/context/AuthContext';
import { P } from '@/config/permissionCodes';
import { extractApiMessage, isDuplicateError } from '@/utils/apiError';

export default function TableManagement() {
  const { hasPermission } = useAuth();

  // Permission flags
  const canAdd    = hasPermission(P.BACK_OFFICE.TABLES.ADD);
  const canEdit   = hasPermission(P.BACK_OFFICE.TABLES.EDIT);
  const canToggle = hasPermission(P.BACK_OFFICE.TABLES.TOGGLE);
  const canDelete = hasPermission(P.BACK_OFFICE.TABLES.DELETE);

  const canAddSection    = hasPermission(P.BACK_OFFICE.SECTIONS.ADD);
  const canEditSection   = hasPermission(P.BACK_OFFICE.SECTIONS.EDIT);
  const canDeleteSection = hasPermission(P.BACK_OFFICE.SECTIONS.DELETE);
  // Show the Sections button only if the user has any section permission
  const canManageSections = canAddSection || canEditSection || canDeleteSection;

  const [tables, setTables] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedSection, setSelectedSection] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("number-asc");

  const [showTableForm, setShowTableForm] = useState(false);
  // Field-level error handed back to TableForm (e.g. duplicate table number).
  const [formServerError, setFormServerError] = useState(null);
  const [showSectionManager, setShowSectionManager] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [selectedTables, setSelectedTables] = useState([]);

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: "", data: null });

  const branches = useMemo(() => {
    const map = new Map();
    sections.forEach(s => {
      if (s.branchId && !map.has(s.branchId)) {
        map.set(s.branchId, { id: s.branchId, name: s.branchName });
      }
    });
    const list = Array.from(map.values());
    return list.length > 1 ? list : [];
  }, [sections]);

  useEffect(() => {
    if (branches.length > 1 && !selectedBranchId) {
      setSelectedBranchId(branches[0].id);
    }
    if (branches.length <= 1) {
      setSelectedBranchId(null);
    }
  }, [branches]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tablesRes, sectionsRes] = await Promise.all([
        tableService.getAllTables(),
        sectionService.getAll(),
      ]);

      const tablesData = tablesRes?.data || tablesRes || [];
      const sectionsData = sectionsRes?.data || sectionsRes || [];

      const sectionMap = {};
      const sectionBranchMap = {};
      sectionsData.forEach((s) => {
        sectionMap[s.id] = s.name;
        sectionBranchMap[s.id] = s.branchId;
      });

      const tablesWithSections = tablesData.map((table) => ({
        id: table.id,
        number: table.tableNumber,
        capacity: table.seatingCapacity,
        isActive: table.isActive,
        status: table.status,
        idSection: table.idSection || null,
        sectionName: sectionMap[table.idSection] || "Unassigned",
        branchId: sectionBranchMap[table.idSection] || null,
        tableShape: table.tableShape || 'square',
      }));

      setTables(tablesWithSections);
      setSections(sectionsData);
      if (!isInitialLoad) {
        showToast("Tables refreshed successfully", "success");
      } else {
        setIsInitialLoad(false);
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      if (!isInitialLoad) {
        showToast("Failed to refresh tables", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else toast(message);
  };

  const handleSaveTable = async (tableData) => {
    setFormServerError(null);

    const payload = {
      id: editingTable?.id || 0,
      tableNumber: tableData.number,
      idSection: tableData.sectionId,
      seatingCapacity: tableData.capacity,
      isActive: tableData.isActive,
      tableShape: tableData.tableShape,
    };

    try {
      if (editingTable) {
        await tableService.updateTable(editingTable.id, payload);
        showToast("Table updated successfully");
      } else {
        await tableService.createTable(payload);
        showToast("Table added successfully");
      }

      await loadData();
      setShowTableForm(false);
      setEditingTable(null);
    } catch (err) {
      console.error("Save failed:", err);
      const message = extractApiMessage(err, "Failed to save table. Please try again.");

      // Duplicate table number is a field problem: keep the form open and mark the
      // field rather than firing a toast the user has to remember.
      if (isDuplicateError(err, message)) {
        setFormServerError({
          field: "number",
          message: `A table numbered "${payload.tableNumber}" already exists. Please use a different number.`,
        });
        showToast("That table number is already taken", "error");
        return;
      }

      showToast(message, "error");
    }
  };

  const handleDeleteTable = (id) => {
    setConfirmDialog({ isOpen: true, type: "delete", data: id });
  };

  const confirmDelete = async () => {
    try {
      await tableService.deleteTable(confirmDialog.data);
      showToast("Table deleted successfully");
      await loadData();
      setSelectedTables([]);
    } catch (err) {
      console.error("Delete failed:", err);
      showToast("Failed to delete table", "error");
    } finally {
      setConfirmDialog({ isOpen: false, type: "", data: null });
    }
  };

  const handleActivate = async (activate = true) => {
    try {
      await Promise.all(
        selectedTables.map((id) =>
          activate ? tableService.activateTable(id) : tableService.deactivateTable(id)
        )
      );
      showToast(`Table(s) ${activate ? "activated" : "deactivated"} successfully`);
      await loadData();
      setSelectedTables([]);
    } catch (err) {
      console.error("Status update failed:", err);
      showToast("Failed to update status", "error");
    }
  };

  const branchSections = useMemo(() => {
    let filtered = sections.filter(s => s.name?.toUpperCase() !== 'MAVERICK_COUNTER');
    if (selectedBranchId) {
      filtered = filtered.filter(s => s.branchId === selectedBranchId);
    }
    return filtered;
  }, [sections, selectedBranchId]);

  // Tables the form should treat as "taken". Scoped to the selected branch because
  // the server's duplicate check runs under the tenantFilter (branch_id = :branchId),
  // so the same table number IS allowed in two different branches. Comparing against
  // every branch here would block a legitimate create for a multi-branch owner.
  // Deliberately not reusing filteredTables — that also applies search/status/section
  // filters, which would let a hidden row slip through as a false negative.
  const tablesInScope = useMemo(
    () => (selectedBranchId ? tables.filter((t) => t.branchId === selectedBranchId) : tables),
    [tables, selectedBranchId]
  );

  const filteredTables = useMemo(() => {
    let items = tables.filter((t) => t.number?.toUpperCase() !== 'MAVERICK_COUNTER');

    if (selectedBranchId) {
      items = items.filter((t) => t.branchId === selectedBranchId);
    }

    if (selectedSection !== "all") {
      items = items.filter((t) => String(t.idSection) === String(selectedSection));
    }
    if (statusFilter !== "all") {
      items = items.filter((t) => (statusFilter === "active" ? t.isActive : !t.isActive));
    }
    if (searchQuery) {
      items = items.filter((t) =>
        t.number?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    switch (sortBy) {
      case "number-asc":
        items.sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));
        break;
      case "number-desc":
        items.sort((a, b) => b.number.localeCompare(a.number, undefined, { numeric: true }));
        break;
      case "capacity-asc":
        items.sort((a, b) => a.capacity - b.capacity);
        break;
      case "capacity-desc":
        items.sort((a, b) => b.capacity - a.capacity);
        break;
      default:
        break;
    }

    return items;
  }, [tables, selectedSection, statusFilter, searchQuery, sortBy, selectedBranchId]);

  const confirmConfig =
    confirmDialog.type === "delete"
      ? {
          title: "Delete Table",
          message: "Are you sure you want to delete this table?",
          confirmText: "Delete",
          type: "danger",
          onConfirm: confirmDelete,
        }
      : null;

  return (
    <div className="min-h-full bg-gray-50 py-6 px-4">
      <div className="w-full space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Table Management</h1>
            <p className="text-sm text-gray-600 mt-0.5">
              Manage restaurant tables and sections
            </p>
          </div>
          <div className="flex gap-2">
            {canManageSections && (
              <button
                onClick={() => setShowSectionManager(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium"
              >
                <Grid size={16} />
                Sections
              </button>
            )}
            {canAdd && (
              <button
                onClick={() => {
                  setEditingTable(null);
                  setShowTableForm(true);
                }}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 text-sm font-medium"
              >
                <Plus size={16} />
                Add Table
              </button>
            )}
          </div>
        </div>

        {/* Branch Tabs */}
        {branches.length > 1 && (
          <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1">
            {branches.map(b => (
              <button
                key={b.id}
                onClick={() => {
                  setSelectedBranchId(b.id);
                  setSelectedSection('all');
                  setSelectedTables([]);
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedBranchId === b.id
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search tables..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
            />
          </div>

          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
          >
            <option value="all">All Sections</option>
            {branchSections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm font-medium cursor-pointer"
          >
            <option value="number-asc">Table Number (Low-High)</option>
            <option value="number-desc">Table Number (High-Low)</option>
            <option value="capacity-asc">Capacity (Low-High)</option>
            <option value="capacity-desc">Capacity (High-Low)</option>
          </select>

          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 text-sm font-medium transition-all disabled:opacity-50"
            title="Refresh tables"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>

          <div className="ml-auto flex gap-2">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-md border ${
                viewMode === "grid" ? "bg-teal-100 border-teal-500" : "border-gray-300"
              }`}
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-md border ${
                viewMode === "list" ? "bg-teal-100 border-teal-500" : "border-gray-300"
              }`}
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        {selectedTables.length > 0 && (canToggle || canDelete) && (
          <div className="flex justify-end gap-2 bg-white border border-gray-200 p-3 rounded-lg">
            {canToggle && (
              <button
                onClick={() => handleActivate(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
              >
                Activate
              </button>
            )}
            {canToggle && (
              <button
                onClick={() => handleActivate(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition"
              >
                Deactivate
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => handleDeleteTable(selectedTables[0])}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
              >
                Delete
              </button>
            )}
          </div>
        )}

        {/* Table List */}
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <div className="flex flex-col items-center justify-center gap-4">
              <RefreshCw size={48} className="animate-spin text-teal-600" />
              <h3 className="text-lg font-semibold text-gray-900">Refreshing tables...</h3>
              <p className="text-gray-600 text-sm">Please wait while we update the data</p>
            </div>
          </div>
        ) : (
          <TableList
            tables={filteredTables}
            viewMode={viewMode}
            selectedTables={selectedTables}
            onSelectTables={setSelectedTables}
            onEdit={(table) => {
              setEditingTable(table);
              setShowTableForm(true);
            }}
            onDelete={handleDeleteTable}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        )}

        {/* Modals */}
        {showTableForm && (
          <TableForm
            table={editingTable}
            sections={branchSections}
            onSave={handleSaveTable}
            serverError={formServerError}
            onClearServerError={() => setFormServerError(null)}
            // Branch-scoped, matching the server's own duplicate check.
            existingTables={tablesInScope}
            onClose={() => {
              setShowTableForm(false);
              setEditingTable(null);
              setFormServerError(null);
            }}
          />
        )}

        {showSectionManager && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <SectionManager
              branchId={selectedBranchId}
              canAdd={canAddSection}
              canEdit={canEditSection}
              canDelete={canDeleteSection}
              onSave={() => {
                loadData();
                setShowSectionManager(false);
              }}
              onClose={() => setShowSectionManager(false)}
            />
          </div>
        )}

        {confirmConfig && (
          <ConfirmDialog
            isOpen={confirmDialog.isOpen}
            onClose={() => setConfirmDialog({ isOpen: false, type: "", data: null })}
            {...confirmConfig}
          />
        )}

      </div>
    </div>
  );
}